import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import {
  Card,
  Skeleton,
  CardContent,
  Typography,
  Stack,
  Autocomplete,
  TextField,
  Switch,
  FormControlLabel,
  CardActions,
  MenuItem,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
// swr
import useSWR from 'swr';
// hooks
import useAuth from '../../hooks/useAuth';
// utils
import axiosSWRFetcher from '../../utils/swrFetcher';
import axios from '../../utils/axios';
import { LoadingButton } from '@mui/lab';

const filter = createFilterOptions();

export default function MatchingKeyword() {
  const { user } = useAuth();
  const { data, isValidating, mutate } = useSWR(user ? `/api/crews/settings/keyword-filter/` : null, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: false});
  const [keywords, setKeywords] = useState([]);
  const [isSubmit, setIsSubmit] = useState(false);
  const [systemKeywords, setSystemKeywords] = useState([]);
  const [isAllowToOpen, setIsAllowToOpen] = useState(user?.crew?.allow_to_open);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (data) {
      setSystemKeywords(data?.system_keywords);
      const userKeywordsNotInSystem = data?.user_keywords.filter(
        (userKeyword) => !data.system_keywords.some(
          (systemKeyword) => systemKeyword.keyword === userKeyword.keyword
        )
      );
      if (userKeywordsNotInSystem.length > 0) {
        setSystemKeywords((prev) => [...prev, ...userKeywordsNotInSystem]);
      }
      setKeywords(data?.user_keywords);
    }
  }, [data]);

  const handleKeywordChange = (event, newValues, reason) => {
    const stringValue = newValues.find((value) => typeof value === 'string');
    const newKeyword = stringValue ? { keyword: stringValue, inputValue: "" } : null;

    if (reason === "createOption" && newKeyword) {
      if (!systemKeywords.some((option) => option.keyword === stringValue)) {
        setSystemKeywords((prev) => [...prev, newKeyword]);
      }
      setKeywords((prev) => {
        if (!prev.some((keyword) => keyword.keyword === newKeyword.keyword)) {
          return [...prev, newKeyword];
        }
        return prev.filter((keyword) => keyword.keyword !== newKeyword.keyword);
      });
    } else {
      const addedValues = newValues.filter(
        (val) => !systemKeywords.some((option) => option.keyword === val.keyword)
      ).map((val) => ({ ...val, inputValue: "" }));

      if (addedValues.length) {
        setSystemKeywords((prev) => [...prev, ...addedValues]);
      }
      setKeywords(newValues);
    }
  };

  const updateKeywordFilter = async () => {
    try {
      setIsSubmit(true);
      await axios.post(`/api/crews/settings/keyword-filter/`, { keywords });
      mutate();
      enqueueSnackbar('키워드 필터가 저장되었습니다.', { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('키워드 필터 저장에 실패했습니다. 다시 시도해주세요.', { variant: 'error' });
    } finally {
      setIsSubmit(false);
    }
  };

  const postAllowToOpen = async (value) => {
    try {
      setIsSubmit(true);
      const postData = {
        is_allow: value,
      }
      const response = await axios.post(
        `/api/crews/settings/allow-to-open/`,
        postData
      );
      enqueueSnackbar(`프로필이 ${value ? '' : '비'}공개 되었습니다.`);
      setIsAllowToOpen(response.data.is_allow);
      setIsSubmit(false);
    } catch (error) {
      console.error(error);
      setIsSubmit(false);
    }
  };

  const handleAllowToOpen = async (value) => {
    if (value !== isAllowToOpen) {
      postAllowToOpen(value);
    }
  }

  if (!data) {
    return (
      <Stack spacing={3}>
        <Card sx={{ border: (theme) => `1px solid ${theme.palette.grey[500_32]}` }}>
          <CardContent>
            <Skeleton variant="rectangular" height={100} />
          </CardContent>
        </Card>
        <Card sx={{ border: (theme) => `1px solid ${theme.palette.grey[500_32]}` }}>
          <CardContent>
            <Skeleton variant="rectangular" height={150} />
          </CardContent>
        </Card>
      </Stack>
    )
  }

  return (
    <Stack spacing={4}>
      <Card sx={{ border: (theme) => `1px solid ${theme.palette.grey[500_32]}` }}>
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                disabled={isSubmit || isValidating}
                checked={isAllowToOpen} onChange={(_, value) => {handleAllowToOpen(value)}} />
            }
            labelPlacement="start"
            label={
              <>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  스피드 매칭 : 프로필 공개하기
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  기업이 먼저 내 프로필을 보고 제안할 수 있도록 프로필을 공개합니다.
                </Typography>
              </>
            }
            sx={{ mx: 0, width: 1, justifyContent: 'space-between' }}
          />
        </CardContent>
      </Card>

      <Card sx={{ border: (theme) => `1px solid ${theme.palette.grey[500_32]}` }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            키워드 필터
          </Typography>
          <Typography variant="body2" paragraph>
            아래 키워드가 포함된 공고를 제외하고 원하는 매칭만 받을 수 있습니다.
          </Typography>
          <Autocomplete
            multiple
            fullWidth
            freeSolo
            clearOnBlur
            disableCloseOnSelect
            handleHomeEndKeys
            value={keywords}
            onChange={handleKeywordChange}
            renderInput={(params) => (
              <TextField 
                {...params}
                fullWidth
                placeholder="제외할 키워드를 입력해주세요."
                helperText={keywords.some(keyword => keyword.keyword.length > 100) ? "키워드는 100글자 미만으로 입력해주세요." : "키워드를 입력 후 Enter를 누르면 추가됩니다."}
                error={keywords.some(keyword => keyword.keyword.length > 100)}
              />
            )}
            getOptionLabel={(option) => {
              if (typeof option === 'string') {
                return option;
              }
              return option.keyword;
            }}
            isOptionEqualToValue={(option, value) => option.keyword === value.keyword}
            renderOption={(props, option) => (
              <MenuItem {...props} key={option.keyword}>
                {option.inputValue ? option.inputValue : option.keyword}
              </MenuItem>
            )}
            filterOptions={(options, params) => {
              const filtered = filter(options, params);

              const matched = filtered.find((option) => option.keyword === params.inputValue);
              if (!matched && params.inputValue !== '') {
                filtered.push({
                  inputValue: `"${params.inputValue}" 키워드 추가하기`,
                  keyword: params.inputValue,
                });
              }
              return filtered;
            }}
            options={systemKeywords?.map((option) => {return {keyword: option.keyword, inputValue: option.inputValue || ''}}) || []}
          />
        </CardContent>

        <CardActions sx={{ px: 3, pb: 3, justifyContent: "flex-end" }}>
          <LoadingButton
            variant="contained"
            size="large"
            disableRipple
            loading={isValidating || isSubmit}
            onClick={updateKeywordFilter}
            disabled={keywords.some(keyword => keyword.keyword.length > 100)}
          >
            저장하기
          </LoadingButton>
          </CardActions>
      </Card>
    </Stack>
  )
};
